import React from 'react';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { AICommandIcon } from '@atlaskit/editor-toolbar';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import type { EditorPluginAIProvider } from '../../../../../types/types';
import { FixSpellingIcon } from '../../../../icons/prompt/fixSpelling';
import { ImproveWritingIcon } from '../../../../icons/prompt/improveWriting';
import { type ConvoAiFeatureId } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItemWithOptions,
	EditorPluginAIConfigItem,
} from '../../config-items';

export const improveWriting: (
	product?: EditorPluginAIProvider['product'],
) => EditorPluginAIConfigItem = (product) => {
	let featureId: ConvoAiFeatureId =
		product !== 'CONFLUENCE' && fg('platform_editor_ai_adf_prompts_in_all_products')
			? 'improve_writing_adf_gpt41mini'
			: 'improve_writing';
	let adfPrompt = product !== 'CONFLUENCE' && fg('platform_editor_ai_adf_prompts_in_all_products');

	// Below experiment is only setup for confluence, so for other products, it will resolve to false.
	if (
		expValEqualsNoExposure('platform_editor_ai_iw_adf_streaming', 'cohort', 'adf_gemini25flash')
	) {
		featureId = 'improve_writing_adf_gemini25flash';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure('platform_editor_ai_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'improve_writing_adf_gpt41mini';
		adfPrompt = true;
	}

	return {
		key: CONFIG_ITEM_KEYS.IMPROVE_WRITING,
		title: aiConfigItemMessages.improveWritingTitle,
		description: aiConfigItemMessages.improveWritingDescription,
		icon: ({ formatMessage }) =>
			fg('platform_editor_ai_aifc_patch_beta_2') ? (
				<AICommandIcon
					label={formatMessage(aiConfigItemMessages.improveWritingTitle)}
					size="small"
				/>
			) : (
				<ImproveWritingIcon />
			),
		intentSchemaId: 'improve_quality_intent_schema.json',
		featureId,
		adfPrompt,
		requiresTextualContent: adfPrompt,
		actions: {
			empty: [
				createInsertBelow({ appearance: 'secondary' }),
				createReplace({ appearance: 'primary' }),
			],
			selection: [
				createInsertBelow({ appearance: 'secondary' }),
				createReplace({ appearance: 'primary' }),
			],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
		canToggleDiffPreview: true,
	};
};

export const improveWritingWithOptions: (
	product?: EditorPluginAIProvider['product'],
) => EditorPluginAIConfigItemWithOptions = (product) => ({
	config: improveWriting(product),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const fixSpellingAndGrammar: (
	product?: EditorPluginAIProvider['product'],
) => EditorPluginAIConfigItem = (product) => {
	let featureId: ConvoAiFeatureId =
		product !== 'CONFLUENCE' && fg('platform_editor_ai_adf_prompts_in_all_products')
			? 'fix_spelling_grammar_adf_gpt41mini'
			: 'fix_spelling_grammar';
	let adfPrompt = product !== 'CONFLUENCE' && fg('platform_editor_ai_adf_prompts_in_all_products');

	// Below experiment is only setup for confluence, so for other products, it will resolve to false.
	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'fix_spelling_grammar_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gemini25flash')
	) {
		featureId = 'fix_spelling_grammar_adf';
		adfPrompt = true;
	}

	return {
		key: CONFIG_ITEM_KEYS.FIX_SPELLING_AND_GRAMMAR,
		title: aiConfigItemMessages.fixSpellingGrammarTitle,
		description: aiConfigItemMessages.fixSpellingGrammarDescription,
		icon: () => <FixSpellingIcon />,
		intentSchemaId: 'grammar_spelling_intent_schema.json',
		featureId,
		adfPrompt,
		requiresTextualContent: adfPrompt,
		actions: {
			empty: [createReplace({ appearance: 'primary' })],
			selection: [createReplace({ appearance: 'primary' })],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
	};
};

export const fixSpellingAndGrammarWithOptions: (
	product?: EditorPluginAIProvider['product'],
) => EditorPluginAIConfigItemWithOptions = (product) => ({
	config: fixSpellingAndGrammar(product),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
